import React, { useState, useEffect, useCallback } from "react";
import { Message, Modal, Button } from "semantic-ui-react";
import useAuthStore from "stores/useAuthStore";
import RuvixxForm from "../../../components/RuvixxForm";
import CampaignService from "../../../services/Campaign";
import ContactService from "../../../services/Contact";
import EntityService from "../../../services/Entity";
import PropTypes from "prop-types";
import { EMAIL_SERVICES, NULL_TEMPLATE } from "../../../constants/Constants";
import Validate from "../../../components/Validate";
import { isTemplateDisabled } from "../../../helpers/emailHelper";
import { useEmailProvider } from "../../../hooks/useEmailProvider";
import { useEmailTemplate } from "../../../hooks/useEmailTemplate";
import { useFileUpload } from "../../../hooks/useFileUpload";
import EmailCampaignSelector from "../../../components/email/EmailCampaignSelector";
import EmailProviderSelector from "../../../components/email/EmailProviderSelector";
import EmailRecipientSelector from "../../../components/email/EmailRecipientSelector";
import EmailTemplateSelector from "../../../components/email/EmailTemplateSelector";
import EmailCCBCCOptions from "../../../components/email/EmailCCBCCOptions";
import EmailFileUpload from "../../../components/email/EmailFileUpload";
import EmailPreview from "../../../components/email/EmailPreview";

const NewEmailForm = ({
  campaignId: initialCampaignId,
  entities,
  contactId,
  contactEmail,
  onSuccess,
  emailService,
  onlySmtp = false,
  followUp = false,
  contactRows = [],
  checkedArr = [],
  forEntity,
  closeParentModal,
}) => {
  const [recipients, setRecipients] = useState([]);
  const [value, setValue] = useState([]);
  const [contactIds, setContactIds] = useState([]);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [ccAccountManagers, setCcAccountManagers] = useState(false);
  const [ccExternalAccountManagers, setCcExternalAccountManagers] =
    useState(false);
  const [ccMenuOpen, setCcMenuOpen] = useState(false);
  const [fromUser, setFromUser] = useState("");
  const [fromDomain, setFromDomain] = useState("");
  const [senderName, setSenderName] = useState(
    useAuthStore.getState().userAuth.full_name
  );
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignId, setCampaignId] = useState(initialCampaignId || null);
  const [campaign, setCampaign] = useState({});
  const [includeAlternativeEmails, setIncludeAlternativeEmails] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sentModal, setSentModal] = useState(false);

  const getSelectedContacts = useCallback(() => {
    let contacts = [];
    if (checkedArr.length > 0) {
      contacts = contactRows.filter(row => checkedArr.includes(row.id));
    }
    return contacts;
  }, [checkedArr, contactRows]);

  const emailProvider = useEmailProvider({
    emailService,
    onlySmtp,
    campaign,
  });

  const templateDisabledFn = useCallback(
    template => {
      return isTemplateDisabled(template, emailProvider.provider);
    },
    [emailProvider.provider]
  );

  const emailTemplate = useEmailTemplate({
    templateDisabledFn,
    campaignTemplates,
  });

  const fileUpload = useFileUpload();

  const fetchCampaign = useCallback(
    async campaignIdToFetch => {
      if (!campaignIdToFetch && initialCampaignId) {
        campaignIdToFetch = initialCampaignId;
      }
      if (campaignIdToFetch) {
        try {
          const campaign = await CampaignService.getCampaign(campaignIdToFetch);
          setCampaign(campaign);
          setCampaignTemplates(campaign.info.selected_templates || []);
        } catch (error) {
          console.error("Error fetching campaign:", error);
        }
      }
    },
    [initialCampaignId]
  );

  const fetchCampaigns = useCallback(async () => {
    try {
      const campaigns = await CampaignService.getCampaignsForFilters(
        contactId ? { contact_id: contactId } : { entity_id: entities[0] }
      );
      const contacts = getSelectedContacts();

      const campaignCounts = contacts
        .flatMap(({ campaign_targets }) => campaign_targets)
        .map(({ campaign_id }) => campaign_id)
        .reduce((acc, campaignId) => {
          acc[campaignId] = (acc[campaignId] || 0) + 1;
          return acc;
        }, {});
      const campaignsBelongingToAllContacts = Object.keys(campaignCounts)
        .filter(campaignId => campaignCounts[campaignId] === contacts.length)
        .map(campaignId => +campaignId);

      const campaignOptions = campaigns
        .filter(
          ({ id }) =>
            contacts.length === 0 ||
            campaignsBelongingToAllContacts.includes(id)
        )
        .map(({ id, name }) => ({
          key: id,
          text: name,
          value: id,
        }));
      setCampaignOptions(campaignOptions);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setCampaignOptions([]);
    }
  }, [contactId, entities, getSelectedContacts]);

  const fetchRecipients = useCallback(async () => {
    try {
      let recipients = [];
      let value = [];
      let contacts = [];

      if (contactId) {
        const emailResponse = await ContactService.getEmailAddresses(contactId);
        recipients = emailResponse.filter(
          emailAddress => emailAddress.enabled && emailAddress.subscribed
        );
        value = emailResponse
          .filter(i => i.is_primary === true)
          .map(i => i.key);
      } else {
        contacts = getSelectedContacts();
        if (contacts.length === 0) {
          let filters = {};
          if (initialCampaignId) {
            filters["campaign_id"] = initialCampaignId;
          }
          if (entities.length) {
            filters["entity_id"] = entities;
          }
          contacts = await ContactService.getEmailableContacts(filters);
        }
        const emailFilter = contact => {
          if (!("email_addresses" in contact)) {
            return [contact];
          }
          if (includeAlternativeEmails) {
            return contact.email_addresses.filter(email => email.enabled);
          } else
            return contact.email_addresses.filter(
              email => !!email.enabled && email.is_primary
            );
        };
        recipients = contacts.flatMap(contact => {
          return emailFilter(contact).map(email => ({
            key: email.id,
            text: email.email,
            value: email.id,
            contact_id: contact.id,
          }));
        });
        value = recipients.map(i => i.key);
      }
      setRecipients(recipients);
      setValue(value);
      setContactIds(contacts.map(contact => contact.id));
    } catch (error) {
      console.error("Error fetching recipients:", error);
      setRecipients([]);
      setValue([]);
    }
  }, [
    contactId,
    getSelectedContacts,
    initialCampaignId,
    entities,
    includeAlternativeEmails,
  ]);

  const handleCampaignChange = useCallback(
    async (_, { value }) => {
      try {
        const campaign = await CampaignService.getCampaign(value);
        setCampaignId(value);
        setCampaign(campaign);
        setCampaignTemplates(campaign.info?.selected_templates || []);
        emailProvider.resetProvider();

        await emailProvider.fetchProviders(campaign);
      } catch (error) {
        console.error("Error changing campaign:", error);
      }
    },
    [emailProvider.resetProvider, emailProvider.fetchProviders]
  );

  const handleSubmit = useCallback(
    async e => {
      const uploads = fileUpload.files.map(f => f.attachment);
      const { isSMTP } = emailProvider.providerChecks;

      // Only validate fromUser if we're not using SMTP
      if (
        emailProvider.checkFromField &&
        !isSMTP &&
        !Validate.emailUsername(fromUser)
      ) {
        setErrorMessage('Invalid value in "From" field');
      } else {
        let fromUserResolved = fromUser;
        if (emailProvider.smtp_config_id) {
          fromUserResolved = emailProvider.smtpAccount.return_addr;
        } else if (emailProvider.provider === EMAIL_SERVICES.MAILGUN) {
          fromUserResolved = `${fromUser}@${emailProvider.mailgunDomainSelected}`;
        }

        try {
          if (contactId && contactId > 0) {
            await ContactService.sendEmail({
              contact_id: contactId,
              template: emailTemplate.template.id,
              attachments: uploads,
              senderName,
              cc,
              bcc,
              ccAccountManagers,
              ccExternalAccountManagers,
              fromUser: fromUserResolved,
              smtp_config_id: emailProvider.smtp_config_id,
              provider: emailProvider.provider,
              footerId: emailTemplate.footerId,
              signatureId: emailTemplate.signatureId,
              campaignId: campaignId,
              emailAddressIds: value,
              follow_up: followUp,
            });
          } else if (forEntity) {
            await EntityService.sendEmail({
              entityId: entities[0],
              template: emailTemplate.template.id,
              attachments: uploads,
              senderName,
              cc,
              bcc,
              ccAccountManagers,
              ccExternalAccountManagers,
              fromUser: fromUserResolved,
              smtp_config_id: emailProvider.smtp_config_id,
              provider: emailProvider.provider,
              footerId: emailTemplate.footerId,
              signatureId: emailTemplate.signatureId,
              campaignId: campaignId,
              follow_up: followUp,
            });
          } else {
            await CampaignService.sendEmail({
              campaignId: campaignId,
              targetId: contactId ? entities[0] : undefined,
              contacts: checkedArr.length > 0 ? checkedArr : contactIds,
              template: emailTemplate.template.id,
              attachments: uploads,
              senderName,
              cc,
              bcc,
              ccAccountManagers,
              ccExternalAccountManagers,
              fromUser: fromUserResolved,
              smtp_config_id: emailProvider.smtp_config_id,
              provider: emailProvider.provider,
              footerId: emailTemplate.footerId,
              signatureId: emailTemplate.signatureId,
              follow_up: followUp,
              ...(includeAlternativeEmails && { emailAddressIds: value }),
            });
          }
          setSentModal(true);
        } catch (error) {
          setErrorMessage(error.message || "Failed to send email");
        }
      }
    },
    [
      fileUpload.files,
      senderName,
      cc,
      bcc,
      emailProvider.providerState,
      emailProvider.smtp_config_id,
      emailProvider.smtpAccount,
      emailProvider.checkFromField,
      emailProvider.mailgunDomainSelected,
      emailProvider.provider,
      emailProvider.providerChecks,
      ccAccountManagers,
      ccExternalAccountManagers,
      value,
      emailTemplate.template.id,
      emailTemplate.footerId,
      emailTemplate.signatureId,
      fromUser,
      campaignId,
      includeAlternativeEmails,
      contactIds,
      contactId,
      forEntity,
      entities,
      checkedArr,
      followUp,
    ]
  );

  const handleSelect = useCallback((e, { value }) => {
    setValue(value);
  }, []);

  const handleChange = useCallback((event, data) => {
    const value = data.type === "checkbox" ? data.checked : data.value;
    const name = data.name;

    switch (name) {
      case "fromUser":
        setFromUser(value);
        break;
      case "senderName":
        setSenderName(value);
        break;
      case "cc":
        setCc(value);
        break;
      case "bcc":
        setBcc(value);
        break;
      case "ccAccountManagers":
        setCcAccountManagers(value);
        break;
      case "ccExternalAccountManagers":
        setCcExternalAccountManagers(value);
        break;
      case "mailgunDomainSelected":
        emailProvider.handleMailgunDomainChange(event, data);
        break;
      default:
        console.warn(`No state setter found for field: ${name}`);
    }
  }, []);

  const handleConfirmationClose = useCallback(() => {
    setSentModal(false);
    closeParentModal();
  }, [closeParentModal]);

  const toggleIncludeAlternativeEmails = useCallback(() => {
    setIncludeAlternativeEmails(!includeAlternativeEmails);
    fetchRecipients();
  }, [includeAlternativeEmails, fetchRecipients]);

  useEffect(() => {
    const init = async () => {
      setFromDomain("email.domain.com");
      try {
        const promises = [
          fetchCampaigns(),
          emailProvider.fetchProviders(),
          emailTemplate.fetchTemplates(),
          emailTemplate.fetchEmailFooters(),
          emailTemplate.fetchEmailSignatures(),
          fetchRecipients(),
        ];
        if (initialCampaignId) {
          promises.push(fetchCampaign(initialCampaignId));
        }
        await Promise.all(promises);
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    init();
  }, []);

  // Clear template if it becomes disabled when provider changes
  useEffect(() => {
    if (emailTemplate.template.id) {
      const disabled = isTemplateDisabled(
        emailTemplate.template,
        emailProvider.provider
      );
      // Only clear if disabled and not already cleared
      if (disabled && emailTemplate.template.id !== NULL_TEMPLATE.id) {
        emailTemplate.setTemplate({ ...NULL_TEMPLATE });
      }
    }
  }, [emailProvider.provider]);

  useEffect(() => {
    if (
      campaign?.id &&
      campaign?.id === initialCampaignId &&
      initialCampaignId
    ) {
      emailProvider.fetchProviders();
    }
  }, [campaign, initialCampaignId, emailProvider.fetchProviders]);

  const { isSMTP, isMailgun } = emailProvider.providerChecks;
  let hasFromUserAddress = !!fromUser;
  if (isSMTP) {
    hasFromUserAddress = emailProvider.smtp_config_id !== null;
  }

  return (
    <>
      <Modal
        open={sentModal}
        onClose={handleConfirmationClose}
        closeOnDimmerClick={false}
      >
        <Modal.Content>
          <Message info>
            <Message.Header>Emails Sent</Message.Header>
          </Message>
          <Button content="ok" onClick={handleConfirmationClose} />
        </Modal.Content>
      </Modal>
      <RuvixxForm
        ready={
          hasFromUserAddress &&
          !!emailTemplate.template.id &&
          (forEntity || value.length > 0) &&
          !!senderName &&
          !!emailProvider.provider &&
          !!campaignId
        }
        onSubmit={handleSubmit}
        onSuccess={!errorMessage ? onSuccess : () => {}}
        submitButtonText="Send"
        successHeader="Email Sent"
        confirmSubmit={true}
        confirmText="Are you sure you want to send this email?"
        ignoreEnterKey={true}
        error={!!errorMessage}
        errorMessage={errorMessage}
      >
        {!initialCampaignId && (
          <EmailCampaignSelector
            campaignId={campaignId}
            campaignOptions={campaignOptions}
            onCampaignChange={handleCampaignChange}
            disabled={onlySmtp}
          />
        )}

        <EmailProviderSelector
          providerOptions={emailProvider.providerOptions}
          providerState={emailProvider.providerState}
          onProviderChange={emailProvider.handleProviderChange}
          disabled={onlySmtp}
          senderName={senderName}
          onSenderNameChange={handleChange}
          fromUser={fromUser}
          onFromUserChange={handleChange}
          fromDomain={fromDomain}
          errorMessage={errorMessage}
          isSMTP={isSMTP}
          smtpOptions={emailProvider.smtpOptions}
          smtp_config_id={emailProvider.smtp_config_id}
          onSmtpChange={emailProvider.handleFromChange}
          isMailgun={isMailgun}
          mailgunDomainOptions={emailProvider.mailgunDomainOptions}
          mailgunDomainSelected={emailProvider.mailgunDomainSelected}
          onMailgunDomainChange={handleChange}
        />

        <EmailRecipientSelector
          recipients={recipients}
          value={value}
          onChange={handleSelect}
          forEntity={forEntity}
          showAlternativeEmailsToggle={checkedArr.length > 0}
          includeAlternativeEmails={includeAlternativeEmails}
          onToggleAlternativeEmails={toggleIncludeAlternativeEmails}
        />

        <EmailCCBCCOptions
          ccMenuOpen={ccMenuOpen}
          onToggleCCMenu={() => setCcMenuOpen(!ccMenuOpen)}
          ccAccountManagers={ccAccountManagers}
          ccExternalAccountManagers={ccExternalAccountManagers}
          cc={cc}
          bcc={bcc}
          onChange={handleChange}
        />

        <EmailTemplateSelector
          template={emailTemplate.template}
          templateOptions={emailTemplate.templateOptions}
          onTemplateChange={emailTemplate.handleTemplateChange}
          footerOptions={emailTemplate.footerOptions}
          onFooterChange={emailTemplate.handleFooterChange}
          signatureOptions={emailTemplate.signatureOptions}
          onSignatureChange={emailTemplate.handleSignatureChange}
        />

        <EmailFileUpload
          uploadInputRef={fileUpload.uploadInputRef}
          files={fileUpload.files}
          onFileChange={fileUpload.handleFileChange}
          onDelete={fileUpload.uploadManagerRef.current.removeUpload}
        />

        <EmailPreview
          subject={emailTemplate.template.subject}
          previewHtml={emailTemplate.previewHtml}
        />
      </RuvixxForm>
    </>
  );
};

NewEmailForm.propTypes = {
  campaignId: PropTypes.number,
  entities: PropTypes.arrayOf(PropTypes.number),
  contactId: PropTypes.number,
  contactEmail: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  emailService: PropTypes.number,
  onlySmtp: PropTypes.bool,
  followUp: PropTypes.bool,
  contactRows: PropTypes.arrayOf(PropTypes.object),
  checkedArr: PropTypes.arrayOf(PropTypes.number),
  forEntity: PropTypes.bool,
  closeParentModal: PropTypes.func,
};

export default NewEmailForm;
